import {
  DeploymentModel,
  InfrastructureMode,
  OperationsAnswers,
  SubPoint,
  SovScoresData,
  SovWeights,
  SovScoreResult,
  FeatureGroupScore,
  IstSelection,
} from '../types/workplace';

/** Default SOV-Weights from the EU Cloud Sovereignty Framework. */
export const DEFAULT_SOV_WEIGHTS: SovWeights = {
  'sov-1': 0.15,
  'sov-2': 0.10,
  'sov-3': 0.10,
  'sov-4': 0.15,
  'sov-5': 0.20,
  'sov-6': 0.15,
  'sov-7': 0.10,
  'sov-8': 0.05,
};

/** All 8 SOV-Objective IDs. */
const SOV_IDS = ['sov-1', 'sov-2', 'sov-3', 'sov-4', 'sov-5', 'sov-6', 'sov-7', 'sov-8'];

/**
 * Calculate the SOV score for a single Provider-Deployment combination.
 * EU formula: Σ (score_n / maxScore) × weight_n × 100
 * Returns null if the combination doesn't exist or scores are incomplete.
 */
export function calculateProviderSovScore(
  providerName: string,
  deployment: DeploymentModel,
  weights: SovWeights,
  sovData: SovScoresData
): { score: number | null; objectiveScores: Record<string, number> | null } {
  const providerData = sovData.providerDeployments[providerName];
  if (!providerData) return { score: null, objectiveScores: null };

  const deploymentScores = providerData.scores[deployment];
  if (!deploymentScores) return { score: null, objectiveScores: null };

  // Validate all 8 SOV-Objective scores are present
  const maxScore = sovData.metadata.maxScore;
  for (const id of SOV_IDS) {
    if (deploymentScores[id] === undefined) return { score: null, objectiveScores: null };
  }

  const objectiveScores: Record<string, number> = {};
  let score = 0;
  for (const id of SOV_IDS) {
    objectiveScores[id] = deploymentScores[id];
    score += (deploymentScores[id] / maxScore) * (weights[id] ?? 0) * 100;
  }

  return { score, objectiveScores };
}

/**
 * Calculate the overall SOV score across multiple Feature Groups / Sub-Points.
 * Arithmetic mean of individual SOV scores.
 * Unassigned groups (no entry in selections) are excluded.
 *
 * When `subPoints` is provided, iterates over sub-points and dispatches by type:
 *   - 'provider-selection' → calculateProviderSovScore()
 *   - 'operations-scoring' → calculateOperationsSovScore() (requires operationsAnswers)
 *
 * When `subPoints` is NOT provided (backward compatibility), uses featureGroupIds
 * with the existing provider-only logic.
 */
export function calculateOverallSovScore(
  selections: Record<string, IstSelection>,
  weights: SovWeights,
  sovData: SovScoresData,
  featureGroupIds: string[],
  operationsAnswers?: OperationsAnswers,
  subPoints?: SubPoint[]
): SovScoreResult {
  const perFeatureGroup: Record<string, FeatureGroupScore> = {};
  const warnings: string[] = [];
  const objectiveSums: Record<string, number> = {};
  let assignedCount = 0;
  let scoreSum = 0;

  for (const id of SOV_IDS) objectiveSums[id] = 0;

  if (subPoints) {
    // --- New sub-point-based path ---
    for (const sp of subPoints) {
      if (sp.type === 'operations-scoring') {
        // Operations scoring sub-point (e.g. 4.2)
        if (!operationsAnswers) {
          perFeatureGroup[sp.id] = { provider: null, deployment: null, sovScore: null, objectiveScores: null };
          continue;
        }

        const { score, objectiveScores } = calculateOperationsSovScore(operationsAnswers, weights);
        perFeatureGroup[sp.id] = {
          provider: null,
          deployment: null,
          sovScore: score,
          objectiveScores,
        };

        if (score === null) {
          continue;
        }

        assignedCount++;
        scoreSum += score;
        for (const id of SOV_IDS) {
          objectiveSums[id] += objectiveScores![id];
        }
      } else {
        // Provider-selection sub-point
        const sel = selections[sp.id];
        if (!sel) {
          perFeatureGroup[sp.id] = { provider: null, deployment: null, sovScore: null, objectiveScores: null };
          continue;
        }

        const { score, objectiveScores } = calculateProviderSovScore(sel.provider, sel.deployment, weights, sovData);
        perFeatureGroup[sp.id] = {
          provider: sel.provider,
          deployment: sel.deployment,
          sovScore: score,
          objectiveScores,
        };

        if (score === null) {
          warnings.push(`No SOV scores for ${sel.provider} / ${sel.deployment} (Sub-Point ${sp.id})`);
          continue;
        }

        assignedCount++;
        scoreSum += score;
        for (const id of SOV_IDS) {
          objectiveSums[id] += objectiveScores![id];
        }
      }
    }
  } else {
    // --- Legacy featureGroupIds path (backward compatibility) ---
    for (const fgId of featureGroupIds) {
      const sel = selections[fgId];
      if (!sel) {
        perFeatureGroup[fgId] = { provider: null, deployment: null, sovScore: null, objectiveScores: null };
        continue;
      }

      const { score, objectiveScores } = calculateProviderSovScore(sel.provider, sel.deployment, weights, sovData);
      perFeatureGroup[fgId] = {
        provider: sel.provider,
        deployment: sel.deployment,
        sovScore: score,
        objectiveScores,
      };

      if (score === null) {
        warnings.push(`No SOV scores for ${sel.provider} / ${sel.deployment} (Feature Group ${fgId})`);
        continue;
      }

      assignedCount++;
      scoreSum += score;
      for (const id of SOV_IDS) {
        objectiveSums[id] += objectiveScores![id];
      }
    }
  }

  // Overall score = arithmetic mean of assigned groups
  const overallScore = assignedCount > 0 ? scoreSum / assignedCount : null;

  // Per-objective = weighted average across assigned groups (raw 0-10 average)
  const perObjective: Record<string, number> = {};
  for (const id of SOV_IDS) {
    perObjective[id] = assignedCount > 0 ? objectiveSums[id] / assignedCount : 0;
  }

  const sealLevel = overallScore !== null ? sovScoreToSealLevel(overallScore) : null;

  return { overallScore, perFeatureGroup, perObjective, sealLevel, warnings };
}

/**
 * Map a SOV score (0–100%) to a SEAL level (0–4).
 * 0–19% → 0, 20–39% → 1, 40–59% → 2, 60–79% → 3, 80–100% → 4
 */
export function sovScoreToSealLevel(score: number): number {
  if (score < 20) return 0;
  if (score < 40) return 1;
  if (score < 60) return 2;
  if (score < 80) return 3;
  return 4;
}

/**
 * Determine whether the deployment question should be shown for a provider.
 * show=false when: (a) uniform mode AND global deployment available, OR (b) single deployment.
 * show=true when: hybrid mode OR global deployment not available for provider.
 */
export function shouldShowDeploymentQuestion(
  providerName: string,
  infrastructureMode: InfrastructureMode,
  globalDeployment: DeploymentModel | null,
  sovData: SovScoresData
): { show: boolean; availableDeployments: DeploymentModel[]; autoDeployment: DeploymentModel | null } {
  const providerData = sovData.providerDeployments[providerName];
  const availableDeployments = providerData?.availableDeployments ?? [];

  // Single deployment → always auto-select, never show question
  if (availableDeployments.length === 1) {
    return { show: false, availableDeployments, autoDeployment: availableDeployments[0] };
  }

  // Uniform mode with global deployment available for this provider
  if (infrastructureMode !== 'hybrid' && globalDeployment && availableDeployments.includes(globalDeployment)) {
    return { show: false, availableDeployments, autoDeployment: globalDeployment };
  }

  // Hybrid mode or global deployment not available → show question
  return { show: true, availableDeployments, autoDeployment: null };
}

/**
 * Map InfrastructureMode to DeploymentModel.
 * 'on-premise' → 'self-hosted', 'cloud' → 'hosted-eu', 'saas' → 'saas', 'hybrid' → null
 */
export function infrastructureToDeployment(mode: InfrastructureMode): DeploymentModel | null {
  switch (mode) {
    case 'on-premise': return 'self-hosted';
    case 'cloud': return 'hosted-eu';
    case 'saas': return 'saas';
    case 'hybrid': return null;
  }
}

/**
 * Validate that the sum of all weights equals 1.0 (±0.001 tolerance).
 */
export function validateWeights(weights: SovWeights): boolean {
  const sum = Object.values(weights).reduce((acc, v) => acc + v, 0);
  return Math.abs(sum - 1.0) <= 0.001;
}

/**
 * Calculate the SOV score for sub-point 4.2 (Operations Scoring).
 * Uses 4 structured questions instead of provider selection.
 * SOV-Objectives are computed from question answers per buildspec Section 5.
 * Scaled weights are hardcoded (SOV-2: 20%, SOV-4: 30%, SOV-6: 30%, SOV-7: 20%).
 * The `weights` parameter is kept for future flexibility but not used.
 */
export function calculateOperationsSovScore(
  answers: OperationsAnswers,
  weights: SovWeights
): { score: number | null; objectiveScores: Record<string, number> | null } {
  const { q1, q2, q3, q4 } = answers;
  if (q1 === null || q2 === null || q3 === null || q4 === null) {
    return { score: null, objectiveScores: null };
  }

  // SOV-Objective calculation per buildspec Section 5
  const sov2 = (q2 + q3) / 2;
  const sov4 = (q1 + q2 + q4) / 3;
  const sov6 = q4;
  const sov7 = (q1 + q3) / 2;

  // Scaled weights (proportional to 100% for SOV-2,4,6,7 only)
  const scaledWeights = { 'sov-2': 0.20, 'sov-4': 0.30, 'sov-6': 0.30, 'sov-7': 0.20 };
  const maxScore = 4;

  const score =
    (sov2 / maxScore) * scaledWeights['sov-2'] * 100 +
    (sov4 / maxScore) * scaledWeights['sov-4'] * 100 +
    (sov6 / maxScore) * scaledWeights['sov-6'] * 100 +
    (sov7 / maxScore) * scaledWeights['sov-7'] * 100;

  const objectiveScores: Record<string, number> = {
    'sov-1': 0, 'sov-2': sov2, 'sov-3': 0, 'sov-4': sov4,
    'sov-5': 0, 'sov-6': sov6, 'sov-7': sov7, 'sov-8': 0,
  };

  return { score, objectiveScores };
}
